import { useContext, useEffect, useMemo, useState } from 'react';
import { FlowForm, FlowFormLeaveContext, useFlowFormGlobal, registerKeyHandler, unregisterKeyHandler } from 't-lib-ui/structure';
import { RouterContext, goTo, useParams } from 't-lib-ui/router';
import { Spinner, DropDownSelector } from 't-lib-ui/widgets';
import { QueryStatus } from 't-lib-ui/connector';
import MainLayout from '../components/MainLayout';
import PageHeader from '../components/PageHeader';
import HeaderAuthButtons from '../components/HeaderAuthButtons';
import DiscoverTile from '../components/DiscoverTile';
import { requestStartCollapsed } from '../components/ThreeColumnLayout';
import { sidebarLeaveHandler } from '../components/LeftSidebarFocus';
import { AppState } from '../context/general';
import { useGetHomeContent, useGetHomeCategory } from '../connector/api';
import { requestHomeScrollToCategory, setPreviewReturnPath, setFeedOpenedFromTile } from './homeScroll';
import { HOME_CATEGORY_SORTS, HomeCategorySort, sortHomeFeeds } from './homeCategorySort';
import './ScrollPage.css';
import './HomeCategoryPage.css';

// Per-device choice of the tile order (a view convenience, so not a synced preference).
const SORT_KEY = 'home-category-sort';
const SORT_CHOICES = HOME_CATEGORY_SORTS.map(s => ({ ...s, searchTextLc: s.text.toLowerCase() }));

const loadSort = (): HomeCategorySort => {
  try {
    const saved = localStorage.getItem(SORT_KEY);
    return HOME_CATEGORY_SORTS.some(s => s.id == saved) ? saved as HomeCategorySort : 'name';
  } catch { return 'name'; }
};

/**
 * /a/home/{category}: all feeds of one Discover category (the "See all" of the Home page), as
 * wrapping rows of tiles, sortable by name, last update or post frequency. The back arrow (or Esc)
 * returns to the category on the Home page.
 */
const HomeCategoryPage: React.FC = () => {
  useFlowFormGlobal();
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const { category: slug = '' } = useParams<{ category?: string }>();

  // The home content (usually cached from the Home page) names the category and dates its file.
  const home = useGetHomeContent(appState.feedEngine.getHomeDate());
  const content = useGetHomeCategory(slug, home.data?.date);
  const title = content.data?.category.title ?? home.data?.categories.find(c => c.slug == slug)?.title ?? 'Discover';
  const [sort, setSort] = useState(loadSort);
  const changeSort = (value: HomeCategorySort) => {
    setSort(value);
    try { localStorage.setItem(SORT_KEY, value); } catch { /* storage unavailable */ }
  };
  const feeds = useMemo(() => content.data ? sortHomeFeeds(content.data.category.feeds, sort) : [], [content.data, sort]);
  const failed = home.status == QueryStatus.Error || content.status == QueryStatus.Error
    || (!!home.data && !home.data.categories.some(c => c.slug == slug));

  const back = () => {
    requestHomeScrollToCategory(slug);
    goTo(routerCtx, '/a/home');
  };

  useEffect(() => {
    registerKeyHandler('home-category-page', { 'Escape': () => { back(); return true; } }, false, 0);
    return () => unregisterKeyHandler('home-category-page');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const openFeed = (feedId: string) => {
    // The preview's back button returns here; on narrow screens open straight into read mode.
    setPreviewReturnPath(`/a/home/${slug}`);
    setFeedOpenedFromTile(feedId);
    requestStartCollapsed();
    goTo(routerCtx, `/a/feed/${feedId}`);
  };

  return (
    <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
    <MainLayout
      selectedFeed="home"
      right={({ onExpand }) => (
        <div className="scrollPage">
          <PageHeader
            title={title}
            onMenu={onExpand}
            onBack={back}
            rightContent={
              // A FlowForm so the header controls join keyboard navigation (left/right between them).
              <FlowForm className="homeCategoryHeaderForm">
                <DropDownSelector
                  className="homeCategorySort"
                  prefix="Sort: "
                  selections={SORT_CHOICES}
                  value={sort}
                  focussable="sideway"
                  sidewaysBehaviour="both"
                  onSelect={v => changeSort(v.id as HomeCategorySort)}
                />
                <HeaderAuthButtons bare />
              </FlowForm>
            }
          />
          <FlowForm main={true}>
            <div className="homeCategoryContent">
              {content.data ? (
                <div className="homeCategoryGrid discoverTiles">
                  {feeds.map(f => <DiscoverTile key={f.id} feed={f} onOpen={() => openFeed(f.id)} />)}
                </div>
              ) : failed ? (
                <p className="homeCategoryMissing">This category is not available anymore.</p>
              ) : (
                <div className="homeCategoryLoading"><Spinner spinning={true} /></div>
              )}
            </div>
          </FlowForm>
        </div>
      )}
      startWithLeftCollapsed={typeof window !== 'undefined' && window.innerWidth <= 750}
    />
    </FlowFormLeaveContext.Provider>
  );
};

export default HomeCategoryPage;
